import { Injectable } from '@angular/core';
import * as ClassBreakInfo from 'esri/renderers/support/ClassBreakInfo';

import { LayerApi } from '../api/layer-api';
import { util } from '../classes/util';
import { ESRI } from '../esri-loader-util';
import { IClassBreakData, IMapcoreMapImageLayer, IMapcoreSubLayer, IQuery, isClassBreaksRenderer, isDynamicDataLayerSource, isGeometryDataSource, isMapImageLayer, isSimpleRenderer, isSymbolWithOutline, isSymbolWithStyle, isUniqueValueRenderer, MapCoreLayer } from '../types';

import { LegendService } from './legend.service';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    constructor(private readonly layerApi: LayerApi,
        private readonly legendService: LegendService) { }

    themeClassBreaks: {
        [layerId: string]: {
            [fieldName: string]: string;
        };
    } = {};

    themeColors: {
        [layerId: string]: {
            [fieldName: string]: {
                [value: string]: __esri.Color;
            };
        };
    } = {};

    mapView: __esri.MapView;

    minMaxes = {
        unique: {
            min: 2,
            max: 100
        },
        range: {
            min: 3,
            max: 25
        }
    };

    getLayerColumnsFromLayer(layer: MapCoreLayer) {
        let retVal: { name: string; alias: string }[];
        if (layer && layer.mapcoreProps && layer.mapcoreProps.columns && layer.mapcoreProps.columns.length) {
            retVal = layer.mapcoreProps.columns
                .map(c => ({ name: c.name, alias: c.alias }))
                .sort(function(a, b) {
                    return a.alias.localeCompare(b.alias, 'en', { sensitivity: 'base', numeric: true });
                });
        }
        return retVal;
    }

    async getLayerClassBreakData(layerId: number, fieldName: string, numSelectedClasses: number, forceUniqueValues: boolean, query: IQuery, columnIsDate: boolean = false, classBreakLabels: string = '[]') {
        let retVal: IClassBreakData;
        const results = await this.layerApi.getClassBreakData(layerId, [fieldName], numSelectedClasses, forceUniqueValues, query, columnIsDate, classBreakLabels);
        if (results && results.length) {
            retVal = results[0].classBreakData;
        }
        return retVal;
    }

    async getClassColors(count: number, currentColors: __esri.Color[], symbol?: __esri.Symbol, useSymbolScheme: boolean = false, useEsriScheme: boolean = false): Promise<[__esri.Color[], __esri.ColorSchemes]> {
        let colors: __esri.Color[] = [];
        const [Color, SymbologyColor] = await Promise.all([ESRI.Color, ESRI.SymbologyColor]);
        const scheme = SymbologyColor.getSchemes({ geometryType: 'polyline', basemap: this.mapView.map.basemap, theme: 'high-to-low' });
        const tenBase = 10;
        const fiveBase = 5;
        const defaultFillOpacity = 0.5;
        if (symbol && (symbol.type === 'simple-fill' || useSymbolScheme) && (!isSymbolWithStyle(symbol) || (isSymbolWithStyle(symbol) && symbol.style !== 'none')) && this.getOpacity(symbol.color) !== 0) {
            // for non-transparent symbols, always use symbol based color gradient for polygons and, if requested, for points/lines
            // if new colors are needed, recalculate colors to make best use of the available color range.
            if (currentColors && currentColors.length && currentColors.length === count) {
                colors = currentColors;
            } else {
                colors = await this.getColorGradient(symbol.color, count);
            }
        } else if (useEsriScheme) {
            // use ESRI default scheme colors keeping current theme colors if present
            const esriColors: __esri.Color[] = [];
            scheme.primaryScheme.colorsForClassBreaks.filter(x => x.numClasses === tenBase)[0].colors.forEach(breakColor => {
                esriColors.push(breakColor);
            });
            scheme.secondarySchemes[0].colorsForClassBreaks.filter(x => x.numClasses === tenBase)[0].colors.forEach(breakColor => {
                esriColors.push(breakColor);
            });
            scheme.secondarySchemes[1].colorsForClassBreaks.filter(x => x.numClasses === fiveBase)[0].colors.forEach(breakColor => {
                esriColors.push(breakColor);
            });
            if (symbol.type === 'simple-fill') {
                esriColors.forEach(c => c.a = defaultFillOpacity);
            }
            for (let i = 0; i < this.minMaxes.unique.max; i++) {
                colors.push(currentColors && currentColors.length > i ? currentColors[i] : esriColors[i]);
            }
        } else {
            // use MapCore default layer colors. If any colors were customized, they will be re-inserted into the color set
            // before assigning them to classes.
            const defaultColors = this.legendService.getDefaultColors();

            for (let i = 0; i < this.minMaxes.unique.max; i++) {
                if (defaultColors.length > i) {
                    colors.push(Color.fromArray(defaultColors[i]));
                } else {
                    colors.push(Color.fromArray(this.legendService.getRandomColorRGB()));
                }
            }

            if (symbol.type === 'simple-fill') {
                colors.forEach(c => c.a = defaultFillOpacity);
            }
        }
        return [colors, scheme];
    }

    private async getColorGradient(baseColor: __esri.Color | number[], length): Promise<__esri.Color[]> {
        const Color = await ESRI.Color;
        const minOpacity = 0.05;
        const color = Array.isArray(baseColor) ? baseColor : baseColor.toRgba();
        const direction = Math.max(color[0], color[1], color[2]) > 127 ? -1 : 1;
        const avoidTooDarkOrLight = 1.5;
        const deltas = color.map(h => {
            const startPoint = direction === 1 ? 255 - h : h;
            return Math.floor(startPoint / (length * avoidTooDarkOrLight) * direction);
        });
        const colors = Array.from({ length }, (_, i) => {
            return new Color([
                color[0] + deltas[0] * i,
                color[1] + deltas[1] * i,
                color[2] + deltas[2] * i,
                Math.max(color[3], minOpacity)
            ]);
        });
        return direction === 1 ? colors.reverse() : colors;
    }

    private getOpacity(color: __esri.Color): number {
        return Array.isArray(color) ? color[3] : color.a;
    }
    async buildNumericRenderer(layer: IMapcoreMapImageLayer, field: string, classBreakInfos: ClassBreakInfo[], symbols: __esri.Symbol[], sublayerColorSets: __esri.Color[][]) {
        const renderers: __esri.ClassBreaksRenderer[] = [];
        const ClassBreaksRenderer = await ESRI.ClassBreaksRenderer;
        for (let i = 0; i < layer.sublayers.length; i++) {
            const renderer = new ClassBreaksRenderer({ field });
            for (const classBreak of classBreakInfos) {
                renderer.addClassBreakInfo(classBreak);
                (<any>renderer.classBreakInfos[renderer.classBreakInfos.length - 1]).value = (<any>classBreak).value;
            }
            const colors = sublayerColorSets.length > i ? sublayerColorSets[i] : sublayerColorSets[0];
            const customizedColors = this.withThemeColors(layer, classBreakInfos, field, colors);
            this.assignColorsToClasses(renderer, symbols[i], customizedColors);
            layer.sublayers.getItemAt(i).renderer = renderer;
            renderers.push(renderer);
        }
        return renderers;
    }

    async buildUniqueValueRenderer(layer: IMapcoreMapImageLayer, field: string, classBreakInfos: ClassBreakInfo[], symbols: __esri.Symbol[], sublayerColorSets: __esri.Color[][]) {
        const renderers: __esri.UniqueValueRenderer[] = [];
        const UniqueValueRenderer = await ESRI.UniqueValueRenderer;

        for (let i = 0; i < layer.sublayers.length; i++) {
            const renderer = new UniqueValueRenderer({ field });

            for (const uniqueValue of classBreakInfos) {
                renderer.addUniqueValueInfo({
                    label: uniqueValue.label,
                    value: (uniqueValue as any).value
                });
            }
            if ((layer.sublayers.getItemAt(i).renderer as any).visualVariables) {
                renderer.visualVariables = (layer.sublayers.getItemAt(i).renderer as any).visualVariables;
            }
            const colors = sublayerColorSets.length > i ? sublayerColorSets[i] : sublayerColorSets[0];
            const customizedColors = this.withThemeColors(layer, classBreakInfos, field, colors);
            this.assignColorsToClasses(renderer, symbols[i], customizedColors);
            layer.sublayers.getItemAt(i).renderer = renderer;
            renderers.push(renderer);
        }
        return renderers;
    }

    private assignColorsToClasses(renderer: __esri.Renderer, baseSymbol: __esri.Symbol, colors?: __esri.Color[]) {
        let infos = [];

        if (isClassBreaksRenderer(renderer)) {
            infos = renderer.classBreakInfos;
        } else if (isUniqueValueRenderer(renderer)) {
            infos = renderer.uniqueValueInfos;
        }
        for (const [index, info] of infos.entries()) {
            const symbol = util.deepClone(baseSymbol);
            if (isSymbolWithStyle(symbol) && symbol.style === 'none') {
                symbol.style = 'solid';
            }
            symbol.color = colors[index];
            if (isSymbolWithOutline(symbol) && symbol.outline &&
                symbol.outline.color.r === baseSymbol.color.r &&
                symbol.outline.color.g === baseSymbol.color.g &&
                symbol.outline.color.b === baseSymbol.color.b) {
                symbol.outline.color = colors[index];
            }
            info.symbol = symbol;
        }
    }

    async updateLayerTheme(layer: IMapcoreMapImageLayer, performValidation: boolean = true, useSymbolScheme?: boolean, useEsriScheme?: boolean) {
        const currentSubLayer: IMapcoreSubLayer = layer.sublayers.getItemAt(0);
        const recordCount = await this.layerApi.getLayerRecordCount(layer.mapcoreProps.id || layer.mapcoreProps.parentId, layer.mapcoreProps.query);
        if (recordCount && recordCount.length && recordCount[0].recordCount > 0
            && (isUniqueValueRenderer(currentSubLayer.renderer) || isClassBreaksRenderer(currentSubLayer.renderer))
            && isDynamicDataLayerSource(currentSubLayer.source) && isGeometryDataSource(currentSubLayer.source.dataSource)) {
            const forceUniqueValues: boolean = isUniqueValueRenderer(currentSubLayer.renderer);
            let numSelectedClasses: number;
            let minClasses = this.minMaxes.range.min;
            let maxClasses = this.minMaxes.range.max;
            if (forceUniqueValues) {
                minClasses = this.minMaxes.unique.min;
                maxClasses = this.minMaxes.unique.max;
            }
            try {
                let errorMessage = '';
                let isRangeThemed: boolean;
                const selectedField = currentSubLayer.renderer.field;
                const selectedColumn = layer.mapcoreProps.columns.find(x => x.name.toLowerCase() === selectedField.toLowerCase());
                const isDate = selectedColumn.databaseDataType === 'DATE' || selectedColumn.name.includes('DATE');
                const savedClassBreakLabels = this.getClassBreakLabels(layer);

                if (isUniqueValueRenderer(currentSubLayer.renderer)) {
                    numSelectedClasses = currentSubLayer.renderer.uniqueValueInfos.length;
                    isRangeThemed = false;
                    // Sets custom labels found within layer in layer_symbol, uses only if the labels are different than their corresponding value
                    if (!savedClassBreakLabels && !layer.mapcoreProps.classBreakLabels) {
                        const tempUniqueLabels = currentSubLayer.renderer.uniqueValueInfos
                            .filter(info => (info as any).label !== (info as any).value)
                            .map(info => [(info as any).value, (info as any).label]);
                        if (tempUniqueLabels.length) {
                            layer.mapcoreProps.classBreakLabels = JSON.stringify(tempUniqueLabels);
                        }
                    }
                } else if (isClassBreaksRenderer(currentSubLayer.renderer)) {
                    numSelectedClasses = currentSubLayer.renderer.classBreakInfos.length;
                    isRangeThemed = true;
                }

                const result = await this.getLayerClassBreakData(layer.mapcoreProps.parentId,
                    selectedField,
                    numSelectedClasses,
                    forceUniqueValues,
                    JSON.parse(currentSubLayer.source.dataSource.query),
                    isDate,
                    savedClassBreakLabels || layer.mapcoreProps.classBreakLabels);

                if (performValidation) {
                    errorMessage = this.validateMaxClasses(result, minClasses, maxClasses, forceUniqueValues);
                }

                if (errorMessage === '') {
                    await this.applyLayerTheme(layer, result, selectedField, forceUniqueValues, isRangeThemed, useEsriScheme, useSymbolScheme, isDate);
                } else {
                    throw new Error(errorMessage);
                }
            }
            catch (err) {
                throw new Error('There was an error loading data, please try again or contact an administrator.');
            }
        } else {
            throw new Error('No records for the current layer. Theme can\'t be applied.');
        }
    }

    private async applyLayerTheme(layer: IMapcoreMapImageLayer, result: any, selectedField: string, forceUniqueValues: boolean, isRangeThemed: boolean, useEsriScheme: boolean, useSymbolScheme: boolean, isDate: boolean) {
        const symbols = await this.getSimpleLayerSymbols(layer);
        const currentColors = this.getSublayerColors(layer.sublayers.getItemAt(0));
        const [colors] = await this.getClassColors(result.ClassBreakInfos.length, currentColors, symbols[0], this.shouldUseEsriScheme(useEsriScheme), this.shouldUseSymbolScheme(useSymbolScheme));

        if ((result.IsNumericData || isDate) && !forceUniqueValues && isRangeThemed) {
            await this.buildNumericRenderer(layer, selectedField, result.ClassBreakInfos, symbols, [colors]);
        } else {
            await this.buildUniqueValueRenderer(layer, selectedField, result.ClassBreakInfos, symbols, [colors]);
        }

        if (!layer.mapcoreProps.classBreakLabels) {
            layer.mapcoreProps.classBreakLabels = this.generateClassBreakLabels(layer);
        }
        layer.refresh();
        window.dispatchEvent(new CustomEvent<string>('symbolChange', { detail: layer.id }));
    }

    private validateMaxClasses(result: any, minClasses: number, maxClasses: number, forceUniqueValues: any): string {
        let errorMessage = '';
        if (result.ClassBreakInfos.length < minClasses) {
            errorMessage = `This field has fewer than ${minClasses} class breaks. Please try changing the layer filter or selecting a different field.`;
        } else if (result.ClassBreakInfos.length > maxClasses) {
            errorMessage = `This field has more than ${maxClasses} class breaks. Please try filtering the layer or selecting a different field.`;
        } else if (result.ClassBreakInfos.length < minClasses && result.IsNumericData && !forceUniqueValues) {
            errorMessage = 'Not enough records for theme.';
        }
        return errorMessage;
    }

    shouldUseEsriScheme(useEsriScheme?: boolean) {
        let useEsri = useEsriScheme;
        if (useEsri === undefined || useEsri === null) {
            useEsri = true; // defaults to true if no theme editor is found
            const themeEditors = document.getElementsByTagName('mc-theme-editor');
            if (themeEditors.length) {
                const useEsriAttrib = themeEditors[0].attributes['use-esri-scheme'];
                if (useEsriAttrib) {
                    useEsri = useEsriAttrib.value === 'true';
                }
            }
        }
        return useEsri;
    }

    shouldUseSymbolScheme(useSymbolScheme?: boolean) {
        let useSymbol = useSymbolScheme;
        if (useSymbol === undefined || useSymbol === null) {
            useSymbol = false; // defaults to false if no theme editor is found
            const themeEditors = document.getElementsByTagName('mc-theme-editor');
            if (themeEditors.length) {
                const useSymbolAttrib = themeEditors[0].attributes['use-symbol-scheme'];
                if (useSymbolAttrib) {
                    useSymbol = useSymbolAttrib.value === 'true';
                }
            }
        }
        return useSymbol;
    }

    public async getSimpleLayerSymbols(layer: IMapcoreMapImageLayer): Promise<__esri.Symbol[]> {
        const symbols: __esri.Symbol[] = [];
        for (let i = 0; i < layer.sublayers.length; i++) {
            symbols.push(await this.getSimpleSublayerSymbol(layer, i));
        }
        return symbols;
    }

    private async getSimpleSublayerSymbol(layer: IMapcoreMapImageLayer, index: number): Promise<__esri.Symbol> {
        let symbol: __esri.Symbol;
        const r = layer.sublayers.getItemAt(index).renderer;
        if (isSimpleRenderer(r)) {
            symbol = r.symbol;
        } else if (isClassBreaksRenderer(r)) {
            symbol = r.classBreakInfos[0].symbol;
        } else if (isUniqueValueRenderer(r)) {
            symbol = r.uniqueValueInfos[0].symbol;
        } else if (layer.mapcoreProps.simpleRenderers) {
            symbol = layer.mapcoreProps.simpleRenderers[index].symbol;
        }
        return symbol;
    }

    public getSublayerColors(sublayer: IMapcoreSubLayer): __esri.Color[] {
        let colors;
        const r = sublayer.renderer;
        if (isSimpleRenderer(r)) {
            colors = [r.symbol.color];
        } else if (isClassBreaksRenderer(r)) {
            colors = r.classBreakInfos.map(i => i.symbol.color);
        } else if (isUniqueValueRenderer(r)) {
            colors = r.uniqueValueInfos.map(i => i.symbol.color);
        }
        return colors;
    }

    getNewRendererForSublayer(sublayer: IMapcoreSubLayer) {
        if (isDynamicDataLayerSource(sublayer.source) && isGeometryDataSource(sublayer.source.dataSource)) {
            const geometryType = sublayer.source.dataSource.geometryType.replace('polyline', 'line');
            return this.getNewRendererDefinitonForGeometryType(geometryType);
        }
    }

    getNewRendererDefinitonForGeometryType(geometryType: string, keepColor?: boolean) {
        const rendererDefinitonToReturn = {
            type: 'simple',
            symbol: this.getSymbolDefinitionForLayerGeometryType(geometryType, keepColor)
        };
        return rendererDefinitonToReturn;
    }

    // Returns the proper renderer definition for the specified geometry type
    getRenderDefinitionForGeometryType(geometryType: string, keepColor: boolean, renderers: any[] = null) {
        if (renderers === null || renderers.length === 0) {
            // Generate new renderer
            return this.getNewRendererDefinitonForGeometryType(geometryType, keepColor);
        } else {
            // These are symbols, not renderers, so handle here.
            if (renderers.filter(x => x.color).length > 0) {
                renderers = renderers.map(x => ({ type: 'simple', symbol: x }));
            }
            let renderer = renderers[0];
            if (renderers.length > 1) {
                renderer = this.legendService.getCorrectRendererGivenGeometryType(geometryType, renderers);
            }
            return this.legendService.getModifiedRenderer(renderer);
        }
    }

    getSymbolDefinitionForLayerGeometryType(geometryType: string, keepColor: boolean = false) {
        const color = this.legendService.getNewColor(this.mapView.map, keepColor);
        return this.getSymbolDefinitionFromGeometry(geometryType, color);
    }

    getSymbolDefinitionFromGeometry(geometryType: string, color: number[], outlineColor: number[] = null) {
        return this.legendService.getSymbolDefinitionFromGeometry(geometryType, color, outlineColor);
    }

    getColorsForField(layer: __esri.Layer, field: string) {
        if (!this.themeColors[layer.id]) {
            this.themeColors[layer.id] = {};
        }

        field = field.toLowerCase();

        if (!this.themeColors[layer.id][field]) {
            this.themeColors[layer.id][field] = {};
        }

        return this.themeColors[layer.id][field];
    }

    async saveColor(layer: __esri.Layer, field: string, value: string, color: string | number[] | __esri.Color) {
        const Color = await ESRI.Color;
        const fieldColors = this.getColorsForField(layer, field);

        fieldColors[value] = new Color(color);
    }

    forgetColor(layer: __esri.Layer, field: string, value: string) {
        const fieldColors = this.getColorsForField(layer, field);

        // tslint:disable-next-line:no-dynamic-delete
        delete fieldColors[value];
    }

    async saveTheme(layer: IMapcoreMapImageLayer, renderer: __esri.UniqueValueRenderer | __esri.ClassBreaksRenderer) {
        const Color = await ESRI.Color;

        const infos = isUniqueValueRenderer(renderer)
            ? renderer.uniqueValueInfos
            : renderer.classBreakInfos;

        if (isClassBreaksRenderer(renderer)) {
            if (!this.themeClassBreaks[layer.id]) {
                this.themeClassBreaks[layer.id] = {};
            }

            this.themeClassBreaks[layer.id][renderer.field.toLowerCase()] = this.generateClassBreakLabels(layer);
        }

        for (const info of infos) {
            this.saveColor(layer, renderer.field, (<any>info).value || info.label, new Color(info.symbol.color));
        }
    }

    getClassBreakLabels(layer: IMapcoreMapImageLayer): string | null {
        const layerClassBreaks = this.themeClassBreaks[layer.id];

        if (layerClassBreaks && isMapImageLayer(layer)) {
            const field = (<any>layer.allSublayers.toArray()[0].renderer).field.toLowerCase();

            return layerClassBreaks[field];
        } else {
            return null;
        }
    }

    generateClassBreakLabels(layer: IMapcoreMapImageLayer) {
        const renderer = layer.allSublayers.toArray()[0].renderer;

        if (!isClassBreaksRenderer(renderer)) {
            return;
        }

        const classBreakLabels = renderer.classBreakInfos.map(cb => [`${cb.minValue}`, `${cb.label}`]);
        const maximumClassBreak = renderer.classBreakInfos.slice(-1)[0];
        classBreakLabels.push([maximumClassBreak.maxValue.toString(), null]);

        return JSON.stringify(classBreakLabels);
    }

    saveClassBreakLabels(layer: IMapcoreMapImageLayer) {
        if (!this.themeClassBreaks[layer.id]) {
            this.themeClassBreaks[layer.id] = {};
        }

        const field = (<any>layer.allSublayers.toArray()[0].renderer).field.toLowerCase();
        this.themeClassBreaks[layer.id][field] = layer.mapcoreProps.classBreakLabels;
    }

    forgetTheme(layer: __esri.Layer) {
        // tslint:disable:no-dynamic-delete
        delete this.themeColors[layer.id];
        delete this.themeClassBreaks[layer.id];
        // tslint:enable:no-dynamic-delete
    }

    withThemeColors(layer: __esri.Layer, classBreaks: any[], field: string, colors: __esri.Color[]) {
        const fieldColors = this.getColorsForField(layer, field);
        colors = colors.slice();

        if (fieldColors) {
            for (const [index, classBreak] of classBreaks.entries()) {
                if (classBreak.value in fieldColors) {
                    colors[index] = fieldColors[classBreak.value];
                }
            }
        }

        return colors;
    }
}
